import { JSX } from "react";

import { Box, Theme } from "@mui/material";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import ReactDOMServer from "react-dom/server";
import { ChartTooltipFormatterParams } from "src/components/Dashboard/interfaces/DashboardComponentParams";
import { IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";

import { TooltipDiffRow } from "../TooltipDiffRow";
import { TooltipEventRow } from "../TooltipEventRow";
import { TooltipUnicodeRow } from "../TooltipUnicodeRow";
import { TooltipWrapper } from "../TooltipWrapper";
import { formatTooltipDate, sortParamsByValue } from "../utils/tooltipUtils";

export function chartTooltipFormatter(
  params: ChartTooltipFormatterParams,
  t: TFunction,
  theme: Theme,
  expectedVisitors: number | null,
  showColorSpan = false,
  intervalTab: IntervalTab = IntervalTab.Day,
  hasWeather = false
): string {
  const list = Array.isArray(params) ? params : [params];
  const first = list[0];
  const data = first.data as unknown[];

  const date = dayjs(data[0] as string);
  const pastDate = data[2] ? dayjs(data[2] as string) : null;

  const tooltipDate = formatTooltipDate(date, pastDate, t);

  const hasData = (data as unknown[]).at(-1);
  if (!hasData) {
    return ReactDOMServer.renderToString(
      <TooltipWrapper date={tooltipDate} theme={theme}>
        <Box>{t("No data")}</Box>
      </TooltipWrapper>
    );
  }

  const sorted = sortParamsByValue(list);

  const diffRows: JSX.Element[] = [];
  const unicodeRows: JSX.Element[] = [];
  const eventRows: JSX.Element[] = [];

  sorted.forEach((param, index) => {
    if (param.seriesName?.toLowerCase().includes("past")) return;

    const d = param.data as unknown[];

    const firstValue = d[1] as number | undefined;
    const secondValue = d[3] as number | string | undefined;
    const unicodeToday = d[4] as number | string | undefined;
    const unicodePast = d[5] as string | undefined;
    const event = (d[7] ?? (d[8] ? "-" : null)) as string | null;
    const eventPast = (d[8] ?? (d[7] ? "-" : null)) as string | null;

    if (typeof secondValue === "string" && hasWeather) {
      unicodeRows.push(
        <TooltipUnicodeRow
          key={`unicode-${index}`}
          param={param}
          firstValue={firstValue}
          secondValue={secondValue}
          thirdValue={unicodeToday}
          forthValue={unicodePast}
          theme={theme}
        />
      );

      eventRows.push(<TooltipEventRow key={`event-${index}`} event={event} eventPast={eventPast} t={t} theme={theme} />);

      return;
    }

    if (event || eventPast) {
      eventRows.push(<TooltipEventRow key={`event-${index}`} event={event} eventPast={eventPast} t={t} theme={theme} />);
      return;
    }

    diffRows.push(
      <TooltipDiffRow
        key={`diff-${index}`}
        param={param}
        firstValue={firstValue}
        secondValue={secondValue as number}
        theme={theme}
        expectedVisitors={expectedVisitors}
        intervalTab={intervalTab}
        showColorSpan={showColorSpan}
        hasWeather={hasWeather}
      />
    );
  });

  return ReactDOMServer.renderToString(
    <TooltipWrapper date={tooltipDate} theme={theme}>
      {[...diffRows, ...unicodeRows, ...eventRows]}
    </TooltipWrapper>
  );
}
